<template>
  <div class="video-detail-container" @click="handleContainerClick">
    <!-- 星空背景容器：不阻挡交互，仅作为氛围背景 -->
    <div id="starry-sky"></div>

    <!-- 返回按钮 -->
    <div class="detail-header">
      <h2 class="detail-title">视频详情</h2>
      <el-button
        :icon="Close"
        @click.stop="handleBack"
        circle
        text
        class="close-button"
      />
    </div>

    <!-- 内容区域 -->
    <div class="detail-content">
      <div v-if="!loading && videoData" class="detail-layout">
        <!-- 左侧：视频展示区域 -->
        <div class="video-section">
          <div class="video-wrapper">
            <!-- 下载按钮和新窗口打开按钮 -->
            <div class="action-buttons-wrapper">
              <VideoSubtitleControl :video="videoData" class="detail-subtitle-control" @change="handleSubtitleChange" />
              <el-button
                v-if="videoData.videoUrl && videoData.status === 'completed'"
                type="primary"
                :icon="downloadLoading ? undefined : Download"
                :loading="downloadLoading"
                @click.stop="handleDownload"
                class="download-button"
              >
                <template v-if="downloadLoading">
                  <el-icon class="button-loading-icon" :size="18"><Loading /></el-icon>
                  下载中...
                </template>
                <template v-else>
                  下载视频
                </template>
              </el-button>
              <!-- <el-button
                type="primary"
                :icon="FullScreen"
                @click.stop="handleOpenInNewWindow"
                class="new-window-button"
                title="在新窗口播放"
              >
                新窗口播放
              </el-button> -->
            </div>
            <div class="video-container" @click.stop="handleVideoContainerClick">
              <video
                v-if="videoData.videoUrl && videoData.status === 'completed'"
                ref="videoElement"
                :src="cachedVideoUrl || displayVideoUrl(videoData)"
                :key="displayVideoUrl(videoData)"
                :controls="!videoLoading"
                controlsList="nodownload"
                referrerpolicy="no-referrer"
                :class="['detail-video', { 'vertical-video': isVerticalVideo, 'horizontal-video': !isVerticalVideo }]"
                @loadedmetadata="handleVideoLoaded"
                @loadstart="handleVideoLoadStart"
                @waiting="handleVideoWaiting"
                @canplay="handleVideoCanPlay"
                @play="handleVideoPlay"
                @pause="handleVideoPause"
                @error="handleVideoError"
                @click.stop
              ></video>
              <template v-if="videoData.videoUrl && videoData.status === 'completed'">
                <!-- 加载和播放状态共用一个互斥分支，避免异步事件切换时同时显示 -->
                <div
                  v-if="videoLoading"
                  class="video-loading-overlay"
                  aria-live="polite"
                  aria-label="Loading video"
                >
                  <el-icon class="loading-icon"><Loading /></el-icon>
                  <span class="loading-text">视频加载中...</span>
                </div>
                <!-- 自定义中央播放按钮 -->
                <div
                  v-else-if="!isPlaying"
                  class="custom-play-button"
                  @click.stop="handlePlayClick"
                >
                  <div class="play-button-inner">
                    <el-icon class="play-icon-large"><VideoPlay /></el-icon>
                  </div>
                </div>
              </template>
              <div v-if="!videoData.videoUrl || videoData.status !== 'completed'" class="video-placeholder-large">
                <div class="placeholder-content-large">
                  <div class="placeholder-icon-large">
                    <el-icon><VideoCamera /></el-icon>
                  </div>
                  <div class="placeholder-status-large">{{ videoData.statusText }}</div>
                  <div v-if="videoData.status === 'in_progress' && hasVideoProgress(videoData)" class="progress-info">
                    <el-progress
                      :percentage="videoData.progress"
                      :stroke-width="6"
                    />
                    <div class="progress-text-large">{{ videoData.progress }}%</div>
                  </div>
                  <div v-if="videoData.status === 'failed'" class="error-container">
                    <div v-if="getFriendlyErrorMessage(videoData.errorMessage)" class="error-friendly">
                      <el-icon class="error-friendly-icon"><WarningFilled /></el-icon>
                      <span>{{ getFriendlyErrorMessage(videoData.errorMessage) }}</span>
                    </div>
                    <div v-if="videoData.errorMessage" class="error-raw">
                      {{ videoData.errorMessage }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：视频信息区域 -->
        <div class="info-section">
          <div class="info-card">
            <h3 class="info-title">基本信息</h3>
            <div class="info-list">
              <div class="info-item">
                
                <div class="info-value prompt-input-wrapper">
                  <div class="textarea-wrapper">
                    <el-input
                      v-model="editablePrompt"
                      type="textarea"
                      :rows="10"
                      placeholder="请输入新的提示词"
                      class="prompt-textarea"
                      @keydown.enter.ctrl="handleRemix"
                    />
                    <el-button
                      :icon="CopyDocument"
                      @click.stop="handleCopyPrompt"
                      class="copy-prompt-button"
                      title="复制提示词"
                      circle
                      text
                    />
                  </div>
                  <el-button
                    v-if="videoData && videoData.status !== 'failed'"
                    type="primary"
                    :loading="remixing"
                    @click.stop="handleRemix"
                    class="remix-button"
                  >
                    {{ remixing ? '修改提交中...' : '修改' }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="resolvedInputReference" class="info-card">
            <h3 class="info-title">参考图片</h3>
            <div class="image-container">
              <ReferenceThumbnail
                :src="resolvedInputReference"
                fit="cover"
                lazy
                class="info-image"
                @dblclick.stop="openImagePreview(resolvedInputReference)"
                title="双击查看大图"
              />
            </div>
          </div>

          <div v-if="resolvedFileUrl" class="info-card">
            <h3 class="info-title">技术信息</h3>
            <div class="image-container">
              <ReferenceThumbnail
                :src="resolvedFileUrl"
                fit="cover"
                lazy
                class="info-image"
                @dblclick.stop="openImagePreview(resolvedFileUrl)"
                title="双击查看大图"
              />
            </div>
          </div>

          <!-- 参考资源（Seedance 多资源） -->
          <div v-if="parsedReferenceFiles && (parsedReferenceFiles.images?.length || parsedReferenceFiles.videos?.length || parsedReferenceFiles.audios?.length)" class="info-card">
            <h3 class="info-title">参考资源</h3>
            <div class="reference-files-section">
              <!-- 参考图片：自适应网格 -->
              <div v-if="parsedReferenceFiles.images?.length" class="ref-group">
                <div class="ref-group-header">
                  <el-icon class="ref-group-icon"><Picture /></el-icon>
                  <span class="ref-group-label">参考图片</span>
                  <span class="ref-group-count">{{ parsedReferenceFiles.images.length }}</span>
                </div>
                <div class="ref-grid ref-image-grid">
                  <div
                    v-for="(img, idx) in parsedReferenceFiles.images"
                    :key="'ref-img-' + idx"
                    class="ref-grid-item"
                    @dblclick.stop="openImagePreview(img.url)"
                    title="双击查看大图"
                  >
                    <ReferenceThumbnail :src="img.url" class="ref-grid-item-img" @click="openImagePreview(img.url)" />
                    <span v-if="img.role" class="ref-role-badge">{{ img.role === 'first_frame' ? '首帧' : img.role === 'last_frame' ? '尾帧' : '参考' }}</span>
                  </div>
                </div>
              </div>
              <!-- 参考视频：横向滚动列表 -->
              <div v-if="parsedReferenceFiles.videos?.length" class="ref-group">
                <div class="ref-group-header">
                  <el-icon class="ref-group-icon"><VideoCamera /></el-icon>
                  <span class="ref-group-label">参考视频</span>
                  <span class="ref-group-count">{{ parsedReferenceFiles.videos.length }}</span>
                </div>
                <div class="ref-video-scroll">
                  <div
                    v-for="(vid, idx) in parsedReferenceFiles.videos"
                    :key="'ref-vid-' + idx"
                    class="ref-video-card"
                  >
                    <ReferenceThumbnail :src="vid.url" media-type="video" class="ref-video-element" />
                    <div class="ref-video-overlay">
                      <el-icon class="ref-video-play-icon"><VideoPlay /></el-icon>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 参考音频：紧凑列表 -->
              <div v-if="parsedReferenceFiles.audios?.length" class="ref-group">
                <div class="ref-group-header">
                  <el-icon class="ref-group-icon"><Headset /></el-icon>
                  <span class="ref-group-label">参考音频</span>
                  <span class="ref-group-count">{{ parsedReferenceFiles.audios.length }}</span>
                </div>
                <div class="ref-audio-list">
                  <div
                    v-for="(aud, idx) in parsedReferenceFiles.audios"
                    :key="'ref-aud-' + idx"
                    class="ref-audio-item"
                  >
                    <div class="ref-audio-index">{{ idx + 1 }}</div>
                    <audio :src="aud.url" preload="none" controls class="ref-audio-player" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <el-empty v-if="!loading && !videoData" description="视频不存在" />
    </div>

    <!-- 图片大图预览（自定义透明背景遮罩） -->
    <div
      v-if="imagePreviewVisible"
      class="image-preview-mask"
      @click="imagePreviewVisible = false"
    >
      <div class="image-preview-wrapper" @click.stop>
        <img :src="imagePreviewUrl" alt="预览图片" class="image-preview-large" />
      </div>
    </div>
  </div>
</template>

<script>
import ReferenceThumbnail from './components/ReferenceThumbnail.vue'
import VideoSubtitleControl from './components/VideoSubtitleControl.vue'
import { displayVideoUrl } from './model/subtitleVersion'
import { referenceDisplayUrl } from './model/referenceUrls'
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Close, VideoCamera, Loading, VideoPlay, Download, FullScreen, CopyDocument, Headset, Picture, WarningFilled } from '@element-plus/icons-vue'
import { getVideoDetail, getSeedanceProgress, getVideoGenerationStatus, remixVideo } from '@/api/video'
import { withSilentMode } from '@/utils/request'
import { normalizeVideoUiStatus, hasVideoProgress, wan3CdWorkflowLabel } from './model/videoProgress'
import { listVolcMentionAssets } from '@/api/volcAsset'
import { mentionAssetsCache } from './model/mentionAssetsCache'
import { getOrCacheVideoUrl } from '@/utils/videoCache'

export default {
  name: 'VideoDetailPage',
  components: {
    ReferenceThumbnail,
    VideoSubtitleControl,
    VideoCamera,
    Picture,
    Headset,
    WarningFilled,
  },
  props: {
    // 作为独立路由使用时，优先使用路由参数 id；
    // 作为覆盖层组件使用时，由父组件通过该 prop 传入视频 ID。
    videoId: {
      type: [String, Number],
      default: null
    },
    // 是否在返回时使用 router.back()。
    // 作为覆盖层嵌入 VideoList 时应传 false，由组件通过 emit 通知父组件关闭。
    useRouterBack: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close', 'subtitle-change'],
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(false)
    const videoData = ref(null)
    const videoLoading = ref(false)
    const videoElement = ref(null)
    const isPlaying = ref(false)
    const isVerticalVideo = ref(false) // 是否为竖版视频
    const editablePrompt = ref('')
    const remixing = ref(false)
    const cachedVideoUrl = ref(null) // 缓存的视频 URL
    const downloadLoading = ref(false)
    const volcAssets = ref([]) // 火山引擎虚拟人像资产列表

    // 解析 asset:// URI 为实际可显示的 sourceUrl
    const resolveAssetUrl = (url) => {
      if (!url || !url.startsWith('asset://')) return url
      const assetId = url.replace('asset://', '')
      const found = volcAssets.value.find(a => a.assetId === assetId)
      return found ? (found.sourceUrl || found.volcUrl || url) : url
    }

    // 加载火山引擎资产列表（用于解析 asset:// URI）
    const loadVolcAssets = async () => {
      if (volcAssets.value.length > 0) return
      try {
        const data = await mentionAssetsCache.load('mentions:all', async () => {
          const res = await listVolcMentionAssets()
          if (res.code !== 200) throw Object.assign(new Error(res.message || 'Asset list unavailable'), { code: res.code })
          return res.data || []
        })
        if (data) volcAssets.value = data
      } catch (e) {
        console.warn('加载虚拟人像资产失败', e)
      }
    }

    // 获取状态文本
    const getStatusText = (status, workflowStatus, platform) => {
      if (platform === 'wan3-cd') {
        const label = wan3CdWorkflowLabel(workflowStatus, status)
        if (label) return label
      }
      const statusMap = {
        'pending': '等待中',
        'queued': '排队中',
        'in_progress': '处理中',
        'completed': '已完成',
        'failed': '失败'
      }
      return statusMap[normalizeVideoUiStatus(status)] || status
    }

    // 获取状态标签类型
    const getStatusTagType = (status) => {
      const typeMap = {
        'pending': 'info',
        'queued': 'info',
        'in_progress': 'warning',
        'completed': 'success',
        'failed': 'danger'
      }
      return typeMap[normalizeVideoUiStatus(status)] || 'info'
    }

    // 格式化时间
    const formatTime = (timestamp) => {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    // 视频加载开始
    const handleVideoLoadStart = () => {
      videoLoading.value = true
    }

    // 视频等待加载
    const handleVideoWaiting = () => {
      videoLoading.value = true
    }

    // 视频可以播放
    const handleVideoCanPlay = () => {
      videoLoading.value = false
    }

    // 视频加载完成处理
    const handleVideoLoaded = (event) => {
      // 视频元数据加载完成
      const videoEl = event?.target
      if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
        const ratio = videoEl.videoWidth / videoEl.videoHeight
        // 宽高比 < 1 认为是竖版视频：优先高度展示，左右留白
        isVerticalVideo.value = ratio < 1
      } else {
        // 无法获取宽高比时，回退为横版处理
        isVerticalVideo.value = false
      }
    }

    // 视频播放
    const handleVideoPlay = () => {
      isPlaying.value = true
    }

    // 视频暂停
    const handleVideoPause = () => {
      isPlaying.value = false
    }

    // 安全地播放视频（检查元素是否仍在 DOM 中）
    const safePlayVideo = (videoEl) => {
      if (!videoEl) return
      // 检查元素是否仍在 DOM 中
      if (!document.contains(videoEl)) {
        return
      }
      // 检查元素是否仍然有效
      if (videoEl.readyState === 0) {
        return
      }
      try {
        const playPromise = videoEl.play()
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            // 忽略 "play() was interrupted" 错误
            if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
              console.warn('视频播放失败:', error)
            }
          })
        }
      } catch (error) {
        // 忽略错误
        console.warn('视频播放异常:', error)
      }
    }

    // 安全地暂停视频
    const safePauseVideo = (videoEl) => {
      if (!videoEl) return
      // 检查元素是否仍在 DOM 中
      if (!document.contains(videoEl)) {
        return
      }
      try {
        videoEl.pause()
      } catch (error) {
        // 忽略错误
        console.warn('视频暂停异常:', error)
      }
    }

    // 点击播放按钮
    const handlePlayClick = () => {
      if (videoElement.value) {
        safePlayVideo(videoElement.value)
      }
    }

    // 点击视频容器（用于切换播放/暂停）
    const handleVideoContainerClick = (e) => {
      // 如果点击的是视频本身，不处理（由原生控件处理）
      if (e.target.tagName === 'VIDEO') {
        return
      }
      // 如果点击的是容器，切换播放/暂停
      if (videoElement.value) {
        if (videoElement.value.paused) {
          safePlayVideo(videoElement.value)
        } else {
          safePauseVideo(videoElement.value)
        }
      }
    }

    // 返回上一页 / 关闭覆盖层
    const handleBack = () => {
      if (props.useRouterBack) {
        router.back()
      } else {
        emit('close')
      }
    }

    // 点击容器：如果点击的是背景空白（不在任何交互区域内），则返回列表
    const handleContainerClick = (event) => {
      const target = event.target
      if (!target) return

      // 排除所有不应该触发返回的区域
      const excludedSelectors = [
        '.video-section',           // 视频展示区域
        '.info-section',            // 信息区域
        '.action-buttons-wrapper',  // 操作按钮区域
        '.close-button',            // 关闭按钮（已有自己的处理）
        '.image-preview-mask',      // 图片预览遮罩
        '.image-preview-wrapper',   // 图片预览容器
        'button',                   // 所有按钮（除了已处理的）
        'input',                    // 所有输入框
        'textarea',                 // 所有文本域
        'video',                    // 视频元素
        '.el-button',               // Element Plus 按钮
        '.el-input',                // Element Plus 输入框
        '.el-textarea',             // Element Plus 文本域
        '.el-progress',             // Element Plus 进度条
        '.el-image',                // Element Plus 图片
        '.el-empty',                // Element Plus 空状态
        '.info-card',               // 信息卡片
        '.video-container',         // 视频容器
        '.video-wrapper',           // 视频包装器
        '.custom-play-button',      // 自定义播放按钮
        '.video-placeholder-large', // 视频占位符
        '.placeholder-content-large', // 占位符内容
        '.el-dialog',               // Element Plus 对话框
        '.el-dialog__wrapper',      // 对话框包裹层
        '.el-slider',               // 区间滑块
        '.el-slider__button',       // 滑块按钮
        '.el-slider__runway',       // 滑块轨道
        '.el-tooltip'               // 可能存在的 tooltip/popover
      ]

      // 检查点击目标是否在任何排除区域内
      for (const selector of excludedSelectors) {
        if (target.closest(selector)) {
          return // 在排除区域内，不触发返回
        }
      }

      // 点击的是背景空白区域，返回列表
      handleBack()
    }

    // 下载视频
    const handleDownload = async () => {
      if (downloadLoading.value) {
        return
      }
      if (!videoData.value?.videoUrl || videoData.value.status !== 'completed') {
        ElMessage.warning('视频不可下载')
        return
      }

      try {
        downloadLoading.value = true
        // 获取视频 URL（优先使用缓存的 blob URL，否则使用原始 URL）
        const sourceUrl = displayVideoUrl(videoData.value)
        const preferredUrl = await getOrCacheVideoUrl(sourceUrl)
        const videoUrl = preferredUrl || sourceUrl
        if (displayVideoUrl(videoData.value) === sourceUrl && videoUrl !== cachedVideoUrl.value && videoUrl?.startsWith('blob:')) {
          cachedVideoUrl.value = videoUrl
        }
        
        // 如果是 blob URL，直接使用现有 blob 地址下载（不再二次 fetch，避免 ERR_FILE_NOT_FOUND）
        if (videoUrl.startsWith('blob:')) {
          const link = document.createElement('a')
          link.href = videoUrl
          link.download = `video_${videoData.value.id || Date.now()}.mp4`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          ElMessage.success('下载开始')
          return
        }

        // 如果是普通 URL，优先使用 fetch 下载（可自定义文件名）
        try {
          const response = await fetch(videoUrl, { referrerPolicy: 'no-referrer' })
          if (!response.ok) {
            throw new Error('下载失败')
          }
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url

          // 从 URL 中提取文件名，或使用默认名称
          const urlParts = videoUrl.split('/')
          const fileName = urlParts[urlParts.length - 1] || `video_${videoData.value.id || Date.now()}.mp4`
          link.download = fileName

          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          ElMessage.success('下载开始')
        } catch (fetchError) {
          // fetch 失败（如 CORS 错误），降级到直接打开 URL
          // 注意：跨域时 download 属性会被忽略，浏览器将使用服务器返回的文件名
          const link = document.createElement('a')
          link.href = videoUrl
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          ElMessage.success('正在下载...')
        }
      } catch (error) {
        console.error('下载视频失败:', error)
        ElMessage.error('下载失败，请稍后重试')
      } finally {
        downloadLoading.value = false
      }
    }

    // 在新标签页打开视频播放
    const handleOpenInNewWindow = () => {
      if (!videoData.value?.videoUrl || videoData.value.status !== 'completed') {
        ElMessage.warning('视频不可播放')
        return
      }

      try {
        // 直接使用原始视频 URL，在新标签页中打开
        const videoUrl = displayVideoUrl(videoData.value)
        window.open(videoUrl, '_blank')
        ElMessage.success('已在新标签页打开视频')
      } catch (error) {
        console.error('打开新标签页失败:', error)
        ElMessage.error('打开新标签页失败，请稍后重试')
      }
    }

    // 视频加载错误处理
    const handleVideoError = (event) => {
      videoLoading.value = false
      if (!videoData.value?.videoUrl || !event?.target) return
      const videoElement = event.target
      const currentSrc = videoElement.src
      const retryCount = Number(videoElement.dataset.retryCount || 0)
      
      // 如果当前使用的是缓存 URL（blob URL），回退到原始 URL
      if (currentSrc.startsWith('blob:') && retryCount === 0) {
        console.warn('缓存视频播放失败，回退到原始 URL')
        // 清除缓存的 URL，使用原始 URL
        if (cachedVideoUrl.value) {
          // 释放 blob URL
          if (cachedVideoUrl.value.startsWith('blob:')) {
            URL.revokeObjectURL(cachedVideoUrl.value)
          }
          cachedVideoUrl.value = null
        }
        videoElement.dataset.retryCount = '1'
        videoElement.src = displayVideoUrl(videoData.value)
        videoElement.load()
        return
      }
      
      // 如果已经是原始 URL 且重试次数未超限，添加时间戳重试
      if (retryCount < 2) {
        videoElement.dataset.retryCount = retryCount + 1
        const separator = displayVideoUrl(videoData.value).includes('?') ? '&' : '?'
        videoElement.src = `${displayVideoUrl(videoData.value)}${separator}_retry=${Date.now()}`
        videoElement.load()
      } else {
        console.error('视频加载失败，已重试多次')
        ElMessage.warning('视频加载失败，请检查网络连接')
      }
    }

    // 初始化星空背景：淡淡星尘 + 稀疏流星（不抢前景内容的戏）
    // 使用 DocumentFragment 批量操作 DOM，提升性能
    const initStarrySky = () => {
      const sky = document.getElementById('starry-sky')
      if (!sky) return

      // 清空旧内容，避免重复挂载产生多层星星
      sky.innerHTML = ''

      const starCount = 60 // 星星数量：适度稀疏
      const meteorCount = 4 // 流星轨道数量：非常少，保持"偶尔划过"的感觉

      // 使用 DocumentFragment 批量添加元素，减少重排
      const fragment = document.createDocumentFragment()

      // 1. 生成静态星星（带轻微呼吸闪烁）
      for (let i = 0; i < starCount; i++) {
        const star = document.createElement('div')
        star.classList.add('star')

        const x = Math.random() * 100
        const y = Math.random() * 100
        const size = Math.random() * 2 + 1 // 1px - 3px
        const duration = Math.random() * 3 + 3 // 3s - 6s，不同步呼吸

        star.style.left = `${x}%`
        star.style.top = `${y}%`
        star.style.width = `${size}px`
        star.style.height = `${size}px`
        star.style.setProperty('--duration', `${duration}s`)

        fragment.appendChild(star)
      }

      // 2. 生成流星（极细、半透明、只在边缘偶尔划过）
      for (let i = 0; i < meteorCount; i++) {
        const meteor = document.createElement('div')
        meteor.classList.add('meteor')

        const x = Math.random() * 100
        const y = Math.random() * 50 // 上半部分为主，减少对核心区域干扰
        const delay = Math.random() * 10 // 0s - 10s，错开出现时间

        meteor.style.left = `${x}%`
        meteor.style.top = `${y}%`
        meteor.style.setProperty('--delay', `${delay}s`)

        fragment.appendChild(meteor)
      }

      // 一次性添加到 DOM，减少重排次数
      sky.appendChild(fragment)
    }

    // 解析 referenceFiles JSON，同时将 asset:// URI 替换为 sourceUrl
    const parsedReferenceFiles = computed(() => {
      const raw = videoData.value?.referenceFiles
      if (!raw) return null
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (!parsed || typeof parsed !== 'object') return null
        // 深层解析 asset:// URI 为实际 URL
        const resolveList = (items) => {
          if (!Array.isArray(items)) return items
          return items.map(item => ({
            ...item,
            url: resolveAssetUrl(referenceDisplayUrl(item))
          }))
        }
        return {
          ...parsed,
          images: resolveList(parsed.images),
          videos: resolveList(parsed.videos),
          audios: resolveList(parsed.audios)
        }
      } catch (e) {
        return null
      }
    })

    // 解析 inputReference 和 fileUrl 中的 asset:// URI
    const resolvedInputReference = computed(() => resolveAssetUrl(videoData.value?.inputReference))
    const resolvedFileUrl = computed(() => resolveAssetUrl(videoData.value?.fileUrl))

    // 监听 videoData 变化，同步 editablePrompt
    watch(() => videoData.value?.prompt, (newPrompt) => {
      if (newPrompt) {
        editablePrompt.value = newPrompt
      }
    }, { immediate: true })

    // 复制提示词
    const handleCopyPrompt = async () => {
      if (!editablePrompt.value || !editablePrompt.value.trim()) {
        ElMessage.warning('暂无内容可复制')
        return
      }

      try {
        await navigator.clipboard.writeText(editablePrompt.value)
        ElMessage.success('复制成功')
      } catch (error) {
        console.error('复制失败:', error)
        // 降级方案：使用传统方法
        const textarea = document.createElement('textarea')
        textarea.value = editablePrompt.value
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        try {
          document.execCommand('copy')
          ElMessage.success('复制成功')
        } catch (err) {
          ElMessage.error('复制失败，请手动复制')
        }
        document.body.removeChild(textarea)
      }
    }

    // 提交重新生成
    const handleRemix = () => {
      if (!editablePrompt.value || !editablePrompt.value.trim()) {
        ElMessage.warning('请输入提示词')
        return
      }

      if (!videoData.value?.taskId) {
        ElMessage.error('任务ID不存在')
        return
      }

      remixing.value = true
      remixVideo({
        originalTaskId: videoData.value.taskId,
        prompt: editablePrompt.value.trim()
      })
        .then((response) => {
          if (response.code === 200) {
            ElMessage.success('提交成功')
            // 返回视频列表页面
            router.push('/videos')
          } else {
            ElMessage.error(response.message || '提交失败')
          }
        })
        .catch((error) => {
          console.error('提交失败:', error)
          ElMessage.error('提交失败: ' + (error.message || '未知错误'))
        })
        .finally(() => {
          remixing.value = false
        })
    }

    // 加载缓存的视频 URL
    const loadCachedVideoUrl = async (videoUrl) => {
      if (!videoUrl) return
      try {
        const cachedUrl = await getOrCacheVideoUrl(videoUrl)
        if (cachedUrl && cachedUrl !== videoUrl && displayVideoUrl(videoData.value) === videoUrl) {
          // 如果是缓存的 blob URL，更新状态
          cachedVideoUrl.value = cachedUrl
        }
      } catch (error) {
        console.warn('加载缓存视频失败:', error)
      }
    }

    const handleSubtitleChange = ({ id, subtitleRemoval }) => {
      if (String(videoData.value?.id) !== String(id)) return
      videoData.value.subtitleRemoval = subtitleRemoval
      emit('subtitle-change', { id, subtitleRemoval })
    }
    watch(() => displayVideoUrl(videoData.value), (url, previousUrl) => {
      cachedVideoUrl.value = null
      isPlaying.value = false
      videoLoading.value = Boolean(url && videoData.value?.status === 'completed')
      // Initial loading and generation completion already warm the cache below.
      if (url && previousUrl) loadCachedVideoUrl(url)
    })

    let progressTimer = null
    let detailVersion = 0
    let disposed = false
    const detailProgressRequests = new Set()
    const stopProgressPolling = () => {
      if (progressTimer != null) clearInterval(progressTimer)
      progressTimer = null
    }
    const refreshVideoProgress = async () => {
      const current = videoData.value
      if (!current?.taskId || ['completed', 'failed'].includes(current.status)) {
        stopProgressPolling()
        return
      }
      if (document.hidden || detailProgressRequests.has(current.taskId)) return
      const version = detailVersion
      detailProgressRequests.add(current.taskId)
      try {
        const response = await withSilentMode(() => current.platform === 'wan3-cd' && current.generationId
          ? getVideoGenerationStatus(current.generationId) : getSeedanceProgress(current.taskId))
        if (disposed || version !== detailVersion || videoData.value?.taskId !== current.taskId) return
        if (response.code !== 200 || !response.data) return
        const item = response.data
        const status = normalizeVideoUiStatus(item.status)
        if (videoData.value.status === 'completed' && status !== 'completed') return
        videoData.value = {
          ...videoData.value,
          taskId: item.taskId || current.taskId,
          generationId: item.generationId || current.generationId,
          workflowStatus: item.workflowStatus || current.workflowStatus,
          status,
          statusText: getStatusText(status, item.workflowStatus || current.workflowStatus, current.platform),
          progress: item.progress ?? null,
          videoUrl: item.videoUrl || videoData.value.videoUrl,
          errorMessage: item.errorMessage || null,
          updatedAt: item.updateTime ?? videoData.value.updatedAt
        }
        if (['completed', 'failed'].includes(status)) stopProgressPolling()
        if (status === 'completed' && item.videoUrl) loadCachedVideoUrl(item.videoUrl)
      } catch (_) {
        // Query errors leave the last known task state intact; the next poll retries.
      } finally {
        detailProgressRequests.delete(current.taskId)
      }
    }
    const startProgressPolling = () => {
      stopProgressPolling()
      if (!videoData.value?.taskId || ['completed', 'failed'].includes(videoData.value.status)) return
      progressTimer = setInterval(refreshVideoProgress, 5000)
      refreshVideoProgress()
    }

    // 组件卸载时清理视频播放状态
    onUnmounted(() => {
      disposed = true
      detailVersion++
      stopProgressPolling()
      if (videoElement.value) {
        try {
          // 暂停视频
          safePauseVideo(videoElement.value)
          // 清空视频源
          videoElement.value.src = ''
          videoElement.value.load()
        } catch (error) {
          // 忽略错误
          console.warn('清理视频状态异常:', error)
        }
      }
      // 释放 blob URL
      if (cachedVideoUrl.value && cachedVideoUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(cachedVideoUrl.value)
      }

      // 清理星空容器，防止潜在的残留 DOM
      const sky = document.getElementById('starry-sky')
      if (sky) {
        sky.innerHTML = ''
      }
    })

    // 加载视频详情
    const loadVideoDetail = () => {
      const version = ++detailVersion
      stopProgressPolling()
      videoData.value = null
      cachedVideoUrl.value = null
      videoLoading.value = false
      // 作为覆盖层组件时优先使用传入的 videoId；否则回退到路由参数
      const idFromProps = props.videoId
      const idFromRoute = route.params.id
      const videoId = idFromProps || idFromRoute
      if (!videoId) {
        ElMessage.error('视频ID不存在')
        router.push('/videos')
        return
      }

      loading.value = true
      getVideoDetail(videoId)
        .then((response) => {
          if (disposed || version !== detailVersion) return
          if (response.code === 200 && response.data) {
            const item = response.data
            videoData.value = {
              id: item.id,
              taskId: item.taskId,
              generationId: item.generationId,
              workflowStatus: item.workflowStatus,
              prompt: item.prompt,
              status: normalizeVideoUiStatus(item.status),
              statusText: getStatusText(item.status, item.workflowStatus, item.platform),
              seconds: item.seconds,
              size: item.size,
              watermark: item.watermark || false,
              videoUrl: item.videoUrl,
              subtitleRemoval: item.subtitleRemoval || null,
              fileUrl: item.fileUrl,
              inputReference: item.inputReference,
              progress: item.progress ?? null,
              platform: item.platform,
              model: item.model,
              referenceFiles: item.referenceFiles || null,
              createdAt: item.createdAt,
              updatedAt: item.updatedAt,
              errorMessage: item.errorMessage
            }
            videoLoading.value = Boolean(item.videoUrl && normalizeVideoUiStatus(item.status) === 'completed')
            startProgressPolling()

            // 异步加载缓存的视频 URL
            if (item.videoUrl && item.status === 'completed') {
              loadCachedVideoUrl(displayVideoUrl(videoData.value))
            }

            // 加载火山引擎资产列表（用于解析 asset:// URI）
            loadVolcAssets()
          } else {
            ElMessage.error(response.message || '获取视频详情失败')
            if (props.useRouterBack === false) {
              emit('close')
            } else {
              router.push('/videos')
            }
          }
        })
        .catch((error) => {
          if (disposed || version !== detailVersion) return
          console.error('获取视频详情失败:', error)
          ElMessage.error('获取视频详情失败')
          if (props.useRouterBack === false) {
            emit('close')
          } else {
            router.push('/videos')
          }
        })
        .finally(() => {
          if (!disposed && version === detailVersion) loading.value = false
        })
    }

    // 监听 videoId 变化（覆盖层模式下，videoId 可能从外部传入）
    watch(() => props.videoId, (newVideoId, oldVideoId) => {
      // 只在 videoId 真正变化时重新加载（避免初始化时重复加载）
      if (newVideoId && newVideoId !== oldVideoId) {
        loadVideoDetail()
      }
    })

    onMounted(() => {
      // 立即加载数据，不等待星空背景
      loadVideoDetail()
      
      // 延迟初始化星空背景，不阻塞首次渲染
      // 使用 nextTick 确保 DOM 已渲染，然后使用 requestAnimationFrame 在下一帧执行
      nextTick(() => {
        requestAnimationFrame(() => {
          initStarrySky()
        })
      })
    })

    const imagePreviewVisible = ref(false)
    const imagePreviewUrl = ref('')

    const openImagePreview = (url) => {
      if (!url) return
      imagePreviewUrl.value = url
      imagePreviewVisible.value = true
    }

    const getFriendlyErrorMessage = (errorMessage) => {
      if (!errorMessage) return null
      const lower = errorMessage.toLowerCase()
      if (lower.includes('face') || lower.includes('real person')) return '似乎包含了未审核的真人，把图片上传到资产库再重试吧～'
      if (lower.includes('sensitive information')) return '内容好像触碰到了一些敏感词，改改提示词再试试吧～'
      if (lower.includes('copyright restrictions') || lower.includes('copyright')) return '内容可能有版权方面的限制，换个描述方式再试试？'
      return null
    }

    return {
      displayVideoUrl,
      handleSubtitleChange,
      hasVideoProgress,
      loading,
      videoData,
      videoLoading,
      Close,
      VideoCamera,
      Loading,
      VideoPlay,
      Download,
      FullScreen,
      CopyDocument,
      videoElement,
      isPlaying,
      isVerticalVideo,
      getStatusTagType,
      formatTime,
      handleVideoLoaded,
      handleVideoLoadStart,
      handleVideoWaiting,
      handleVideoCanPlay,
      handleVideoPlay,
      handleVideoPause,
      handlePlayClick,
      handleVideoContainerClick,
      handleBack,
      editablePrompt,
      remixing,
      handleRemix,
      handleCopyPrompt,
      handleDownload,
      downloadLoading,
      cachedVideoUrl,
      handleContainerClick,
      handleOpenInNewWindow,
      handleVideoError,
      imagePreviewVisible,
      imagePreviewUrl,
      openImagePreview,
      parsedReferenceFiles,
      resolvedInputReference,
      resolvedFileUrl,
      getFriendlyErrorMessage,
      WarningFilled,
    }
  }
}
</script>

<style scoped>
.detail-subtitle-control { margin-bottom: 14px; }
.video-detail-container {
  width: 100%;
  min-height: 100vh;
  /* 基底深色背景，叠加星空容器形成深邃夜空 */
  background: #ffffff;
  color: #000000;
  padding: 20px;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
}

/* --- 星空背景容器：深邃径向渐变 + 不抢戏 --- */
#starry-sky {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1; /* 永远在内容层之下 */
  background: #ffffff;
  overflow: hidden;
  pointer-events: none; /* 鼠标点击完全穿透，不影响任何交互 */
}

/* --- 静态星星样式：像尘埃一样微弱，有“呼吸感” --- */
.star {
  position: absolute;
  background: #ffffff;
  border-radius: 50%;
  opacity: 0.2; /* 默认很暗 */
  animation: twinkle var(--duration) ease-in-out infinite;
}

/* --- 流星样式：极细、半透明拖尾，偶尔划过 --- */
.meteor {
  position: absolute;
  top: 50%;
  left: 50%;
  height: 2px;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.5) 100%
  );
  opacity: 0;
  transform: rotate(-45deg);
  animation: meteor-shoot 5s linear infinite; /* 5 秒一轮，但只有前 20% 可见 */
  animation-delay: var(--delay);
}

/* 星星呼吸闪烁：亮度在 0.2 - 0.6 之间轻微变化 */
@keyframes twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

/* 流星划过：短暂出现，其余时间完全透明等待 */
@keyframes meteor-shoot {
  0% {
    transform: translate(300px, -300px) rotate(-45deg);
    opacity: 0;
    width: 0;
  }
  10% {
    opacity: 1; /* 出现 */
    width: 100px; /* 拖尾拉长 */
  }
  20% {
    transform: translate(-100px, 100px) rotate(-45deg); /* 划过对角线一段距离 */
    opacity: 0; /* 迅速消失 */
    width: 0;
  }
  100% {
    /* 剩下 80% 时间完全不可见，制造“偶尔划过”的节奏感 */
    opacity: 0;
  }
}

/* 氛围光效果 */
.video-detail-container::before {
  content: '';
  position: fixed;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  /* 调整氛围光为略微偏蓝的暗色发光效果 */
  background: radial-gradient(circle, rgba(15, 76, 129, 0.12) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.detail-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #000000;
  position: relative;
  z-index: 1;
}

.detail-title::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #00C6FF;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
}

.close-button {
  font-size: 20px;
  width: 40px;
  height: 40px;
  padding: 0;
  background: rgba(245, 247, 250, 0.5);
  border: 1px solid rgba(228, 231, 237, 0.5);
  color: #606266;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.close-button:hover {
  background: rgba(0, 198, 255, 0.1);
  border-color: #00C6FF;
  color: #1875F0;
  transform: scale(1.05);
}

.close-button :deep(.el-icon) {
  font-size: 20px;
}

.detail-content {
  min-height: calc(100vh - 100px);
  position: relative;
  z-index: 1;
}

.detail-layout {
  display: flex;
  gap: 24px;
  max-width: 1600px;
  margin: 0 auto;
}

/* 左侧视频区域 */
.video-section {
  flex: 1;
  min-width: 0;
}

.video-wrapper {
  position: sticky;
  top: 20px;
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

.action-buttons-wrapper {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.download-button {
  /* 顶部“下载视频”按钮采用幽灵/深色半透明风格，弱化视觉存在感 */
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #000000;
  font-weight: 500;
  box-shadow: none;
  transition: all 0.2s ease;
  padding: 8px 20px;
}

.download-button:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.download-button:active {
  transform: translateY(0);
}

.new-window-button {
  /* 顶部“新窗口播放”按钮采用同样的幽灵/深色半透明风格 */
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #000000;
  font-weight: 500;
  box-shadow: none;
  transition: all 0.2s ease;
  padding: 8px 20px;
}

.new-window-button:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.new-window-button:active {
  transform: translateY(0);
}

.button-loading-icon {
  margin-right: 6px;
  animation: rotate 1s linear infinite;
}

.video-container {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(24, 117, 240, 0.25), 0 0 60px rgba(0, 198, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  aspect-ratio: 16 / 9; /* 固定展示区域为 16:9，高度统一 */
  max-height: calc(100vh - 160px); /* 横屏 / 竖屏统一最大高度 */
  display: flex;
  align-items: center;
  justify-content: center; /* 让视频在容器中完全居中，黑边均匀分布 */
}

.detail-video {
  width: 100%;
  height: 100%;
  display: block;
  margin: 0 auto;
  /* 默认按横版处理：铺满容器，减少上下留白 */
  object-fit: cover;
  object-position: center center;
  border-radius: 0;
  background: #000;
}

/* 竖版视频：优先高度，左右留空，避免被裁剪 */
.vertical-video {
  width: auto;
  height: 100%;
  object-fit: contain;
}

/* 横版视频：保持铺满容器 */
.horizontal-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 美化视频播放控件（WebKit浏览器） */
.detail-video::-webkit-media-controls-panel {
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.75) 100%);
}

.detail-video::-webkit-media-controls-play-button {
  display: none !important; /* 隐藏原生播放按钮，使用自定义按钮 */
}

/* 隐藏浏览器在画面中央额外绘制的播放/加载控件，统一由上层状态显示 */
.detail-video::-webkit-media-controls-overlay-play-button {
  display: none !important;
}

.detail-video::-webkit-media-controls-start-playback-button {
  display: none !important;
}

.detail-video::-webkit-media-controls-loading-panel {
  display: none !important;
}

/* 自定义中央播放按钮 */
.custom-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  cursor: pointer;
  transition: all 0.3s ease;
}

.play-button-inner {
  width: auto;
  height: auto;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  transition: all 0.3s ease;
}

.custom-play-button:hover .play-button-inner {
  transform: scale(1.15);
}

.custom-play-button:active .play-button-inner {
  transform: scale(0.95);
}

.play-icon-large {
  font-size: 80px;
  color: rgba(255, 255, 255, 0.9);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.6));
  transition: all 0.3s ease;
}

.detail-video::-webkit-media-controls-current-time-display,
.detail-video::-webkit-media-controls-time-remaining-display {
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  font-weight: 500;
  font-size: 13px;
}

.detail-video::-webkit-media-controls-mute-button,
.detail-video::-webkit-media-controls-volume-slider-container {
  opacity: 0.9;
}

.detail-video::-webkit-media-controls-mute-button:hover,
.detail-video::-webkit-media-controls-fullscreen-button:hover {
  opacity: 1;
  transform: scale(1.1);
  transition: all 0.2s ease;
}

.detail-video::-webkit-media-controls-timeline {
  background-color: transparent;
  border-radius: 2px;
  height: 4px;
}

.detail-video::-webkit-media-controls-timeline::-webkit-media-slider-thumb {
  background: linear-gradient(135deg, #1875F0 0%, #00C6FF 100%);
  border-radius: 50%;
  width: 12px;
  height: 12px;
}

.detail-video::-webkit-media-controls-volume-slider {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.detail-video::-webkit-media-controls-volume-slider::-webkit-media-slider-thumb {
  background: linear-gradient(135deg, #1875F0 0%, #00C6FF 100%);
  border-radius: 50%;
}

.video-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #090909;
  border-radius: 0;
  z-index: 30;
  pointer-events: none;
  isolation: isolate;
}

.loading-icon {
  font-size: 48px;
  color: #fff;
  animation: rotate 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #fff;
  font-size: 16px;
}

.video-placeholder-large {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
}

.placeholder-content-large {
  text-align: center;
  padding: 40px;
}

.placeholder-icon-large {
  font-size: 80px;
  color: #c0c4cc;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.placeholder-status-large {
  font-size: 18px;
  color: #909399;
  margin-bottom: 20px;
}

.progress-info {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.progress-text-large {
  text-align: center;
  margin-top: 12px;
  font-size: 16px;
  color: #606266;
  font-weight: 500;
}

.error-container {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.error-friendly {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fffbf0;
  border: 1px solid #faecd8;
  border-radius: 8px;
  color: #b8860b;
  font-size: 14px;
}

.error-friendly-icon {
  font-size: 16px;
  flex-shrink: 0;
  color: #e6a23c;
}

.error-raw {
  padding: 8px 12px;
  background: #f9f9f9;
  border-radius: 4px;
  color: #c0c4cc;
  font-size: 11px;
  line-height: 1.6;
  word-break: break-all;
}

/* 右侧信息区域 */
.info-section {
  flex: 0 0 500px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-card {
  /* 右侧信息卡片改为深色背景，适配整体深色主题 */
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.9);
  transform: translateY(-2px);
}

.info-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
}

.info-title::before {
  content: '';
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, #2563EB 0%, #3B82F6 100%);
  border-radius: 2px;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-label {
  flex-shrink: 0;
  color: #4B5563;
  font-size: 14px;
  min-width: 60px;
  width: 60px;
}

.info-value {
  flex: 1;
  color: #000000;
  font-size: 14px;
  word-break: break-all;
}

.prompt-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.prompt-textarea {
  width: 100%;
}

.textarea-wrapper {
  position: relative;
  width: 100%;
}

.copy-prompt-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(107, 114, 128, 0.5);
  transition: all 0.2s ease;
  z-index: 10;
}

.copy-prompt-button:hover {
  background: rgba(255, 255, 255, 0.8);
  color: #3B82F6;
  transform: scale(1.05);
}

.copy-prompt-button:active {
  transform: scale(0.95);
}

.prompt-textarea :deep(.el-textarea__inner) {
  line-height: 1.8;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  padding: 15px;
  /* 输入区域比卡片稍亮一点以区分层级 */
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  word-break: break-word;
  white-space: pre-wrap;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Consolas', monospace;
  resize: vertical;
  transition: all 0.3s ease;
  /* 让滚动条更细、更圆滑 */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(156, 163, 175, 0.6) transparent;
}

/* 自定义“基本信息”输入框滚动条（WebKit） */
.prompt-textarea :deep(.el-textarea__inner::-webkit-scrollbar) {
  width: 8px;
}

.prompt-textarea :deep(.el-textarea__inner::-webkit-scrollbar-track) {
  background: transparent;
}

.prompt-textarea :deep(.el-textarea__inner::-webkit-scrollbar-thumb) {
  background: linear-gradient(180deg, rgba(156, 163, 175, 0.4), rgba(107, 114, 128, 0.7));
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: padding-box;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 0 6px rgba(15, 23, 42, 0.6);
}

.prompt-textarea :deep(.el-textarea__inner::-webkit-scrollbar-thumb:hover) {
  background: linear-gradient(180deg, rgba(209, 213, 219, 0.6), rgba(156, 163, 175, 0.9));
}

.prompt-textarea :deep(.el-textarea__inner):focus {
  border-color: #60A5FA;
  box-shadow: 0 0 0 1px rgba(96, 165, 250, 0.35);
}

.remix-button {
  align-self: flex-end;
  min-width: 120px;
  height: 40px;
  /* 右侧核心操作按钮采用更沉稳的品牌蓝色调 */
  background: linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%);
  border: none;
  border-radius: 20px;
  color: #fff;
  font-weight: 500;
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.45);
  transition: all 0.3s ease;
}

.remix-button:hover {
  background: linear-gradient(90deg, #3B82F6 0%, #2563EB 100%);
  box-shadow: 0 8px 22px rgba(37, 99, 235, 0.55);
  transform: translateY(-2px);
}

.remix-button:active {
  transform: translateY(0);
}

.prompt-text {
  line-height: 1.8;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  padding: 15px;
  background: #f9fafb;
  border-left: 4px solid #2563EB;
  border-radius: 12px;
  display: block;
  height: 400px;
  max-height: 400px;
  overflow-y: auto;
  word-break: break-word;
  white-space: pre-wrap;
  width: 100%;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Consolas', monospace;
  border-top: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

/* 美化滚动条 */
.prompt-text::-webkit-scrollbar {
  width: 10px;
}

.prompt-text::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #f5f7fa 0%, #e4e7ed 100%);
  border-radius: 10px;
  border: 1px solid #e4e7ed;
}

.prompt-text::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #409eff 0%, #66b1ff 100%);
  border-radius: 10px;
  border: 2px solid transparent;
  background-clip: padding-box;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.3);
  transition: all 0.3s ease;
}

.prompt-text::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #66b1ff 0%, #85c1ff 100%);
  box-shadow: 0 3px 6px rgba(64, 158, 255, 0.4);
}

.prompt-text::-webkit-scrollbar-thumb:active {
  background: linear-gradient(180deg, #337ecc 0%, #409eff 100%);
}

/* 图片展示区域 */
.image-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-image {
  max-width: 150px;
  max-height: 200px;
  width: auto;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.9);
  /* 更明显的边框，避免和其他区域颜色相融 */
  border: 2px solid rgba(37, 99, 235, 0.95);
  background-clip: padding-box;
  object-fit: contain;
  transition: all 0.18s ease;
  cursor: pointer;
}

/* 自定义图片预览遮罩（背景透明，仅居中展示图片） */
.image-preview-mask {
  position: fixed;
  inset: 0;
  /* 半透明遮罩并对后端内容应用模糊（现代浏览器支持 backdrop-filter） */
  background: rgba(10, 10, 10, 0.45);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  /* 让遮罩淡入，体验更柔和 */
  transition: background 160ms ease, backdrop-filter 160ms ease;
}

.image-preview-wrapper {
  max-width: 80vw;
  max-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-preview-large {
  max-width: 100%;
  max-height: 100%;
  border-radius: 16px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.85);
  object-fit: contain;
  /* 预览时使用明显的白色边框，确保与遮罩/背景区分 */
  border: 6px solid rgba(255, 255, 255, 0.95);
}

.info-image:hover {
  transform: scale(1.06);
  box-shadow: 0 12px 30px rgba(24, 117, 240, 0.28);
  filter: brightness(1.06);
  /* hover 时边框颜色更醒目 */
  border-color: #60A5FA;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .detail-layout {
    flex-direction: column;
  }

  .info-section {
    flex: 1;
    width: 100%;
  }

  .video-wrapper {
    position: static;
  }
}

@media (max-width: 768px) {
  .video-detail-container {
    padding: 12px;
  }

  .detail-title {
    font-size: 20px;
  }

  .detail-layout {
    gap: 16px;
  }

  .video-wrapper,
  .info-card {
    padding: 16px;
  }

  .info-section {
    flex: 1;
  }

  .download-button {
    padding: 8px 20px;
    font-size: 14px;
  }

  .action-buttons-wrapper {
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  
  .download-button,
  .new-window-button {
    flex: 1;
    min-width: 120px;
  }
}

/* 参考资源区域样式 */
.reference-files-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ref-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ref-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.ref-group-icon {
  font-size: 16px;
  color: #409eff;
}

.ref-group-label {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.ref-group-count {
  font-size: 11px;
  color: #909399;
  background: #f0f2f5;
  padding: 1px 8px;
  border-radius: 10px;
  font-weight: 500;
}

/* ---- 图片网格 ---- */
.ref-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.ref-grid-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  background: #f5f7fa;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ref-grid-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 10px rgba(64, 158, 255, 0.2);
  transform: translateY(-2px);
}

.ref-grid-item-img {
  width: 100%;
  height: 100%;
}

.ref-grid-item-img :deep(.el-image) {
  width: 100%;
  height: 100%;
}

.ref-role-badge {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.65));
  color: #fff;
  font-size: 11px;
  text-align: center;
  padding: 8px 4px 4px;
  line-height: 1;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* ---- 视频横向滚动 ---- */
.ref-video-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
  scroll-behavior: smooth;
}

.ref-video-scroll::-webkit-scrollbar {
  height: 6px;
}

.ref-video-scroll::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 3px;
}

.ref-video-scroll::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}

.ref-video-scroll::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}

.ref-video-card {
  position: relative;
  width: 160px;
  height: 100px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  background: #1a1a2e;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ref-video-card:hover {
  border-color: #409eff;
  box-shadow: 0 2px 10px rgba(64, 158, 255, 0.2);
}

.ref-video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ref-video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  transition: background 0.2s ease;
}

.ref-video-card:hover .ref-video-overlay {
  background: rgba(0, 0, 0, 0.35);
}

.ref-video-play-icon {
  font-size: 28px;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  transition: transform 0.2s ease;
}

.ref-video-card:hover .ref-video-play-icon {
  transform: scale(1.15);
}

/* ---- 音频紧凑列表 ---- */
.ref-audio-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ref-audio-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  transition: all 0.2s ease;
}

.ref-audio-item:hover {
  background: #ecf5ff;
  border-color: #d9ecff;
}

.ref-audio-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ref-audio-player {
  height: 32px;
  flex: 1;
  min-width: 0;
  border-radius: 20px;
}

.ref-audio-player::-webkit-media-controls-panel {
  background: #fff;
  border-radius: 20px;
}

/* 响应式：小屏时图片网格缩小 */
@media (max-width: 768px) {
  .ref-image-grid {
    grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
    gap: 8px;
  }

  .ref-video-card {
    width: 120px;
    height: 75px;
  }
}
/* --- dark studio visual system --- */
.video-detail-container {
  --video-page: #000;
  --video-surface: #171717;
  --video-surface-raised: #222;
  --video-surface-soft: #2a2a2a;
  --video-line: #303030;
  --video-line-strong: #444;
  --video-text: #f1f1f1;
  --video-muted: #999;
  --video-dim: #6f6f6f;
  --video-accent: #5bd8b3;
  --el-bg-color: #252525;
  --el-bg-color-overlay: #292929;
  --el-fill-color-blank: #222;
  --el-fill-color-light: #303030;
  --el-fill-color: #333;
  --el-text-color-primary: #f1f1f1;
  --el-text-color-regular: #ccc;
  --el-text-color-secondary: #999;
  --el-text-color-placeholder: #777;
  --el-border-color: #444;
  --el-border-color-light: #383838;
  --el-border-color-lighter: #333;
  --el-color-primary: #f1f1f1;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 28px clamp(18px, 3vw, 52px) 48px;
  overflow: auto;
  background: var(--video-page);
  color: var(--video-text);
  color-scheme: dark;
  font-family: Inter, "PingFang SC", "Microsoft YaHei", sans-serif;
}

#starry-sky {
  position: fixed;
  inset: 0;
  z-index: 0;
  opacity: .2;
  background:
    radial-gradient(circle at 17% 14%, rgba(91, 216, 179, .1), transparent 28%),
    radial-gradient(circle at 84% 4%, rgba(255, 255, 255, .06), transparent 22%),
    #000;
}

.video-detail-container::before {
  position: fixed;
  inset: 0;
  z-index: 0;
  width: auto;
  height: auto;
  background: linear-gradient(180deg, rgba(255,255,255,.025), transparent 32%);
}

.detail-header,
.detail-content { position: relative; z-index: 1; }
.detail-header {
  max-width: 1600px;
  margin: 0 auto 26px;
  padding: 0 4px;
}
.detail-title {
  color: var(--video-text);
  font-size: clamp(22px, 2vw, 30px);
  font-weight: 650;
  letter-spacing: 0;
}
.detail-title::before {
  width: 7px;
  height: 7px;
  margin-right: 10px;
  background: var(--video-accent);
  box-shadow: 0 0 14px rgba(91,216,179,.55);
}
.close-button {
  width: 40px;
  height: 40px;
  border: 1px solid var(--video-line);
  border-radius: 50%;
  color: var(--video-muted);
  background: transparent;
}
.close-button:hover {
  border-color: var(--video-line-strong);
  color: var(--video-text);
  background: var(--video-surface);
  transform: none;
}

.detail-layout {
  width: min(1600px, 100%);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 430px);
  gap: 24px;
  margin: 0 auto;
  align-items: start;
}
.video-section { min-width: 0; }
.video-wrapper {
  position: sticky;
  top: 20px;
  padding: 0;
  background: transparent;
}
.detail-subtitle-control {
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}
.action-buttons-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.download-button,
.new-window-button {
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid var(--video-line-strong);
  border-radius: 20px;
  color: #171717;
  background: #eee;
  box-shadow: none;
  font-weight: 600;
}
.download-button:hover,
.new-window-button:hover {
  border-color: #fff;
  color: #000;
  background: #fff;
  box-shadow: none;
  transform: translateY(-1px);
}
.video-container {
  min-height: min(68vh, 720px);
  aspect-ratio: 16 / 9;
  border: 1px solid #303030;
  border-radius: 20px;
  background: #090909;
  box-shadow: 0 20px 60px rgba(0,0,0,.42), inset 0 1px rgba(255,255,255,.04);
}
.detail-video { border-radius: 19px; }
.video-placeholder-large {
  height: 100%;
  aspect-ratio: auto;
  border: 0;
  border-radius: 19px;
  background: radial-gradient(circle at 50% 38%, #292929 0, #171717 46%, #0c0c0c 100%);
}
.placeholder-content-large { padding: 32px; }
.placeholder-icon-large {
  margin-bottom: 16px;
  color: #777;
  font-size: 56px;
}
.placeholder-status-large { margin-bottom: 16px; color: #c7c7c7; font-size: 15px; }
.progress-info { max-width: 360px; }
.progress-text-large { margin-top: 10px; color: #999; font-size: 13px; }
.error-friendly {
  border-color: #65423f;
  border-radius: 10px;
  color: #e2a39b;
  background: #2a1b1b;
}
.error-raw { color: #9d8585; background: #211919; }
.custom-play-button .play-button-inner {
  width: auto;
  height: auto;
  border: 0;
  border-radius: 0;
  background: transparent;
  backdrop-filter: none;
}
.play-icon-large { color: #fff; font-size: 42px; filter: drop-shadow(0 1px 4px rgba(0, 0, 0, .8)); }

.info-section { flex: none; width: auto; gap: 14px; }
.info-card {
  padding: 20px;
  border: 1px solid var(--video-line);
  border-radius: 16px;
  background: rgba(23,23,23,.92);
  box-shadow: 0 14px 34px rgba(0,0,0,.18);
}
.info-card:hover {
  border-color: #414141;
  background: #1b1b1b;
  box-shadow: 0 18px 42px rgba(0,0,0,.26);
  transform: translateY(-1px);
}
.info-title {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom-color: var(--video-line);
  color: var(--video-text);
  font-size: 15px;
  font-weight: 650;
}
.info-title::before { width: 34px; background: var(--video-accent); }
.info-value { color: var(--video-text); }
.prompt-textarea :deep(.el-textarea__inner) {
  min-height: 190px;
  padding: 13px;
  border: 1px solid #383838;
  border-radius: 12px;
  color: #e8e8e8;
  background: #101010;
  box-shadow: none;
  font-family: inherit;
  resize: vertical;
}
.prompt-textarea :deep(.el-textarea__inner:focus) {
  border-color: #666;
  box-shadow: 0 0 0 1px rgba(255,255,255,.08);
}
.prompt-textarea :deep(.el-textarea__inner::placeholder) { color: #666; }
.copy-prompt-button { color: #777; }
.copy-prompt-button:hover { color: var(--video-text); background: #2d2d2d; }
.remix-button {
  min-width: 104px;
  height: 38px;
  border: 1px solid #55c891;
  border-radius: 19px;
  color: #07130f;
  background: var(--video-accent);
  box-shadow: 0 8px 20px rgba(22,142,98,.2);
}
.remix-button:hover {
  border-color: #82ebc9;
  color: #07130f;
  background: #82ebc9;
  box-shadow: 0 10px 24px rgba(22,142,98,.28);
  transform: translateY(-1px);
}
.image-container { min-height: 120px; padding: 6px 0; }
.info-image {
  max-width: 100%;
  max-height: 230px;
  border: 1px solid #444;
  border-radius: 12px;
  background: #0d0d0d;
  box-shadow: none;
}
.info-image:hover { border-color: #777; box-shadow: 0 12px 28px rgba(0,0,0,.32); filter: none; }
.ref-group-header { margin-bottom: 2px; }
.ref-group-icon { color: var(--video-accent); }
.ref-group-label { color: #cfcfcf; }
.ref-group-count { color: #aaa; background: #303030; }
.ref-grid-item,
.ref-video-card {
  border-color: #3b3b3b;
  border-radius: 10px;
  background: #101010;
}
.ref-grid-item:hover,
.ref-video-card:hover { border-color: #696969; box-shadow: 0 6px 18px rgba(0,0,0,.24); transform: translateY(-1px); }
.ref-video-scroll::-webkit-scrollbar-track { background: #202020; }
.ref-video-scroll::-webkit-scrollbar-thumb { background: #4a4a4a; }
.ref-audio-item { border-color: #383838; border-radius: 10px; background: #101010; }
.ref-audio-item:hover { border-color: #555; background: #1e1e1e; }
.ref-audio-index { background: #3c8d75; }
.ref-audio-player::-webkit-media-controls-panel { background: #252525; }
.image-preview-mask { background: rgba(0,0,0,.78); backdrop-filter: blur(14px); }
.image-preview-large { border: 1px solid #555; border-radius: 14px; box-shadow: 0 28px 80px rgba(0,0,0,.6); }

@media (max-width: 1100px) {
  .detail-layout { grid-template-columns: minmax(0, 1fr) 360px; }
}
@media (max-width: 900px) {
  .detail-layout { grid-template-columns: 1fr; }
  .video-wrapper { position: static; }
  .info-section { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; }
  .info-section .info-card:first-child { grid-column: 1 / -1; }
}
@media (max-width: 640px) {
  .video-detail-container { padding: 18px 12px 32px; }
  .detail-header { margin-bottom: 18px; }
  .detail-layout { gap: 14px; }
  .video-container { min-height: 0; border-radius: 14px; }
  .detail-video, .video-placeholder-large { border-radius: 13px; }
  .action-buttons-wrapper { justify-content: space-between; }
  .download-button { width: auto; flex: 0 0 auto; }
  .info-section { display: flex; }
  .info-card { padding: 16px; }
}

/* Keep the detail view within the viewport; long reference lists scroll inside their card. */
.video-detail-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  padding-top: 16px;
  padding-bottom: 16px;
  overflow: hidden;
}
.detail-header {
  flex: 0 0 auto;
  width: min(1600px, 100%);
  box-sizing: border-box;
  align-self: center;
  justify-content: space-between;
  margin: 0 auto 14px;
}
.detail-content { flex: 1 1 auto; min-height: 0; }
.detail-layout { height: 100%; min-height: 0; align-items: stretch; }
.video-section { display: flex; flex-direction: column; min-height: 0; }
.video-wrapper { position: static; display: flex; flex: 1 1 auto; flex-direction: column; min-height: 0; }
.action-buttons-wrapper { flex: 0 0 auto; margin-bottom: 8px; }
.video-container { flex: 1 1 auto; width: 100%; min-height: 0; max-height: none; aspect-ratio: auto; }
.detail-video, .video-placeholder-large { min-height: 0; }
.info-section { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
.info-section > .info-card { flex: 0 0 auto; min-height: 0; }
.info-section > .info-card:first-child { flex: 1.2 1 0; }
.info-section > .info-card:has(.reference-files-section) { display: flex; flex: .8 1 0; flex-direction: column; overflow: hidden; }
.info-section > .info-card:has(.reference-files-section) .info-title { flex: 0 0 auto; }
.info-section > .info-card:not(:has(.reference-files-section)) { padding: 14px; }
.info-section > .info-card:first-child .prompt-textarea :deep(.el-textarea__inner) { min-height: 0; height: min(18vh, 170px); resize: none; }
.reference-files-section { flex: 1 1 auto; min-height: 0; overflow-x: hidden; overflow-y: auto; padding-right: 6px; scrollbar-width: thin; scrollbar-color: #555 #202020; overscroll-behavior: contain; }
.reference-files-section::-webkit-scrollbar { width: 7px; }
.reference-files-section::-webkit-scrollbar-track { border-radius: 8px; background: #202020; }
.reference-files-section::-webkit-scrollbar-thumb { border: 1px solid #202020; border-radius: 8px; background: linear-gradient(180deg, #666, #444); }
.reference-files-section::-webkit-scrollbar-thumb:hover { background: #777; }

@media (max-width: 900px) {
  .detail-layout { display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; }
  .video-section, .video-wrapper, .video-container { min-height: 0; }
  .info-section { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: minmax(0, 1fr); align-items: stretch; gap: 10px; }
  .info-section > .info-card:has(.reference-files-section), .info-section > .info-card:first-child { grid-column: auto; }
  .prompt-textarea :deep(.el-textarea__inner) { min-height: 0; height: min(18vh, 150px); }
}
@media (max-width: 640px) {
  .video-detail-container { padding: 10px 10px 12px; }
  .detail-header { margin-bottom: 8px; }
  .detail-title { font-size: 20px; }
  .close-button { width: 34px; height: 34px; }
  .detail-layout { grid-template-rows: minmax(0, 1.1fr) minmax(0, .9fr); gap: 8px; }
  .action-buttons-wrapper { margin-bottom: 6px; }
  .download-button { min-height: 32px; }
  .info-section { grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1.2fr) minmax(0, .8fr); }
  .info-section > .info-card:has(.reference-files-section), .info-section > .info-card:first-child { grid-column: auto; }
  .info-card { padding: 10px; }
  .info-title { margin-bottom: 8px; padding-bottom: 6px; }
  .prompt-textarea :deep(.el-textarea__inner) { height: 100%; }
}
</style>

